import { useState, useEffect } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { 
  X, Users, UserPlus, Search, Gift, Star, 
  Check, XCircle, Crown, Loader2, Send,
  Trophy, Zap, UserMinus
} from "lucide-react";

const API_URL = process.env.REACT_APP_BACKEND_URL || '';
const TOKEN_KEY = 'fruitCrush_token';

export function FriendsModal({ isOpen, onClose }) {
  const [friends, setFriends] = useState([]);
  const [pendingRequests, setPendingRequests] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("friends"); // friends, requests, search
  const [actionLoading, setActionLoading] = useState(null);
  const [giftModal, setGiftModal] = useState(null); // { friendId, friendName }
  const [giftAmount, setGiftAmount] = useState(1);
  
  // Fetch friends list
  const fetchFriends = async () => {
    const token = localStorage.getItem(TOKEN_KEY);
    if (!token) return;
    
    try {
      const response = await fetch(`${API_URL}/api/friends`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        setFriends(data.friends || []);
        setPendingRequests(data.pending_requests || []);
      }
    } catch (error) {
      console.error('Failed to fetch friends:', error);
    } finally {
      setIsLoading(false);
    }
  };
  
  useEffect(() => {
    if (isOpen) {
      fetchFriends();
    }
  }, [isOpen]);
  
  // Search for players
  const handleSearch = async () => {
    if (searchQuery.length < 2) return;
    
    setIsSearching(true);
    const token = localStorage.getItem(TOKEN_KEY);
    
    try {
      const response = await fetch(`${API_URL}/api/friends/search?query=${encodeURIComponent(searchQuery)}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        setSearchResults(data.players || []);
      }
    } catch (error) {
      console.error('Search failed:', error);
    } finally {
      setIsSearching(false);
    }
  };
  
  // Add friend
  const addFriend = async (playerName) => {
    setActionLoading(playerName);
    const token = localStorage.getItem(TOKEN_KEY);
    
    try {
      const response = await fetch(`${API_URL}/api/friends/add`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ friend_name: playerName })
      });
      
      if (response.ok) {
        // Refresh data
        await fetchFriends();
        await handleSearch(); // Refresh search results
      }
    } catch (error) {
      console.error('Failed to add friend:', error);
    } finally {
      setActionLoading(null);
    }
  };
  
  // Accept friend request
  const acceptRequest = async (friendId) => {
    setActionLoading(friendId);
    const token = localStorage.getItem(TOKEN_KEY);
    
    try {
      const response = await fetch(`${API_URL}/api/friends/accept/${friendId}`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        await fetchFriends();
      }
    } catch (error) {
      console.error('Failed to accept request:', error);
    } finally {
      setActionLoading(null);
    }
  };
  
  // Decline friend request
  const declineRequest = async (friendId) => {
    setActionLoading(friendId);
    const token = localStorage.getItem(TOKEN_KEY);
    
    try {
      const response = await fetch(`${API_URL}/api/friends/decline/${friendId}`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        await fetchFriends();
      }
    } catch (error) {
      console.error('Failed to decline request:', error);
    } finally {
      setActionLoading(null);
    }
  };
  
  // Remove friend
  const removeFriend = async (friendId) => {
    setActionLoading(friendId);
    const token = localStorage.getItem(TOKEN_KEY);
    
    try {
      const response = await fetch(`${API_URL}/api/friends/${friendId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        await fetchFriends();
      }
    } catch (error) {
      console.error('Failed to remove friend:', error);
    } finally {
      setActionLoading(null);
    }
  };
  
  // Send gift
  const sendGift = async (friendId, giftType, amount) => {
    setActionLoading(`gift-${friendId}`);
    const token = localStorage.getItem(TOKEN_KEY);
    
    try {
      const response = await fetch(`${API_URL}/api/friends/send-gift`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          friend_id: friendId,
          gift_type: giftType,
          amount: amount
        })
      });
      
      if (response.ok) {
        setGiftModal(null);
        // Could show a success toast here
      }
    } catch (error) {
      console.error('Failed to send gift:', error);
    } finally {
      setActionLoading(null);
    }
  };
  
  if (!isOpen) return null;
  
  return (
    <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={onClose}>
      <Card 
        className="w-full max-w-lg bg-gradient-to-br from-indigo-900 via-purple-900 to-pink-900 border-purple-500/50 shadow-2xl max-h-[85vh] overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="p-4 border-b border-white/10 bg-black/20">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-cyan-400 to-blue-500 flex items-center justify-center">
                <Users className="w-6 h-6 text-white" />
              </div>
              <div>
                <h2 className="text-xl font-bold text-white">Friends</h2>
                <p className="text-purple-200 text-sm">{friends.length} friends</p>
              </div>
            </div>
            <Button variant="ghost" onClick={onClose} className="text-white hover:bg-white/10">
              <X className="w-5 h-5" />
            </Button>
          </div>
          
          {/* Tabs */}
          <div className="flex gap-2 mt-4">
            <Button
              variant={activeTab === "friends" ? "default" : "ghost"}
              size="sm"
              onClick={() => setActiveTab("friends")}
              className={activeTab === "friends" 
                ? "bg-purple-500 text-white" 
                : "text-white/70 hover:text-white hover:bg-white/10"
              }
            >
              <Users className="w-4 h-4 mr-1" />
              Friends
            </Button>
            <Button
              variant={activeTab === "requests" ? "default" : "ghost"}
              size="sm"
              onClick={() => setActiveTab("requests")}
              className={activeTab === "requests" 
                ? "bg-purple-500 text-white" 
                : "text-white/70 hover:text-white hover:bg-white/10"
              }
            >
              <UserPlus className="w-4 h-4 mr-1" />
              Requests
              {pendingRequests.length > 0 && (
                <Badge className="ml-1 bg-red-500 text-white text-xs px-1">{pendingRequests.length}</Badge>
              )}
            </Button>
            <Button
              variant={activeTab === "search" ? "default" : "ghost"}
              size="sm"
              onClick={() => setActiveTab("search")}
              className={activeTab === "search" 
                ? "bg-purple-500 text-white" 
                : "text-white/70 hover:text-white hover:bg-white/10"
              }
            >
              <Search className="w-4 h-4 mr-1" />
              Search
            </Button>
          </div>
        </div>
        
        {/* Content */}
        <div className="p-4 overflow-y-auto max-h-[55vh]">
          {isLoading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="w-8 h-8 text-purple-400 animate-spin" />
            </div>
          ) : (
            <>
              {/* Friends Tab */}
              {activeTab === "friends" && (
                <div className="space-y-3">
                  {friends.length === 0 ? (
                    <div className="text-center py-8">
                      <Users className="w-12 h-12 text-purple-400/50 mx-auto mb-3" />
                      <p className="text-white/70">No friends yet</p>
                      <p className="text-white/50 text-sm">Search for players to add friends!</p>
                    </div>
                  ) : (
                    friends.map((friend) => (
                      <div 
                        key={friend.id}
                        className="flex items-center justify-between p-3 bg-white/10 rounded-xl"
                      >
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-full bg-gradient-to-br from-purple-400 to-pink-500 flex items-center justify-center text-white font-bold">
                            {friend.player_name?.[0]?.toUpperCase() || '?'}
                          </div>
                          <div>
                            <div className="flex items-center gap-2">
                              <span className="text-white font-semibold">{friend.player_name}</span>
                              {friend.has_battle_pass && (
                                <Crown className="w-4 h-4 text-yellow-400" />
                              )}
                            </div>
                            <div className="flex items-center gap-3 text-xs text-white/60">
                              <span className="flex items-center gap-1">
                                <Trophy className="w-3 h-3" />
                                Level {friend.level}
                              </span>
                              <span className="flex items-center gap-1">
                                <Zap className="w-3 h-3" />
                                {friend.total_xp} XP
                              </span>
                            </div>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => setGiftModal({ friendId: friend.id, friendName: friend.player_name })}
                            className="text-yellow-400 hover:text-yellow-300 hover:bg-yellow-400/10"
                          >
                            <Gift className="w-4 h-4" />
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => removeFriend(friend.id)}
                            disabled={actionLoading === friend.id}
                            className="text-red-400 hover:text-red-300 hover:bg-red-400/10"
                          >
                            {actionLoading === friend.id ? (
                              <Loader2 className="w-4 h-4 animate-spin" />
                            ) : (
                              <UserMinus className="w-4 h-4" />
                            )}
                          </Button>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              )}
              
              {/* Requests Tab */}
              {activeTab === "requests" && (
                <div className="space-y-3">
                  {pendingRequests.length === 0 ? (
                    <div className="text-center py-8">
                      <UserPlus className="w-12 h-12 text-purple-400/50 mx-auto mb-3" />
                      <p className="text-white/70">No pending requests</p>
                    </div>
                  ) : (
                    pendingRequests.map((request) => (
                      <div 
                        key={request.id}
                        className="flex items-center justify-between p-3 bg-white/10 rounded-xl"
                      >
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-full bg-gradient-to-br from-cyan-400 to-blue-500 flex items-center justify-center text-white font-bold">
                            {request.player_name?.[0]?.toUpperCase() || '?'}
                          </div>
                          <span className="text-white font-semibold">{request.player_name}</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <Button
                            size="sm"
                            onClick={() => acceptRequest(request.id)}
                            disabled={actionLoading === request.id}
                            className="bg-green-500 hover:bg-green-600"
                          >
                            {actionLoading === request.id ? (
                              <Loader2 className="w-4 h-4 animate-spin" />
                            ) : (
                              <Check className="w-4 h-4" />
                            )}
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => declineRequest(request.id)}
                            disabled={actionLoading === request.id}
                            className="text-red-400 hover:bg-red-400/10"
                          >
                            <XCircle className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              )}
              
              {/* Search Tab */}
              {activeTab === "search" && (
                <div className="space-y-4">
                  <div className="flex gap-2">
                    <Input
                      type="text"
                      placeholder="Search players..."
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
                      className="bg-white/10 border-white/20 text-white placeholder:text-white/50"
                    />
                    <Button
                      onClick={handleSearch}
                      disabled={isSearching || searchQuery.length < 2}
                      className="bg-purple-500 hover:bg-purple-600"
                    >
                      {isSearching ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                      ) : (
                        <Search className="w-4 h-4" />
                      )}
                    </Button>
                  </div>
                  
                  {searchResults.length > 0 && (
                    <div className="space-y-2">
                      {searchResults.map((player) => (
                        <div 
                          key={player.id}
                          className="flex items-center justify-between p-3 bg-white/10 rounded-xl"
                        >
                          <div className="flex items-center gap-3">
                            <div className="w-10 h-10 rounded-full bg-gradient-to-br from-orange-400 to-red-500 flex items-center justify-center text-white font-bold">
                              {player.player_name?.[0]?.toUpperCase() || '?'}
                            </div>
                            <div>
                              <span className="text-white font-semibold">{player.player_name}</span>
                              <p className="text-xs text-white/60">Level {player.level}</p>
                            </div>
                          </div>
                          {player.status === "friend" ? (
                            <Badge className="bg-green-500/20 text-green-300">Friends</Badge>
                          ) : player.status === "request_sent" ? (
                            <Badge className="bg-yellow-500/20 text-yellow-300">Request Sent</Badge>
                          ) : player.status === "request_received" ? (
                            <Button
                              size="sm"
                              onClick={() => acceptRequest(player.id)}
                              disabled={actionLoading === player.id}
                              className="bg-green-500 hover:bg-green-600"
                            >
                              Accept
                            </Button>
                          ) : (
                            <Button
                              size="sm"
                              onClick={() => addFriend(player.player_name)}
                              disabled={actionLoading === player.player_name}
                              className="bg-purple-500 hover:bg-purple-600"
                            >
                              {actionLoading === player.player_name ? (
                                <Loader2 className="w-4 h-4 animate-spin" />
                              ) : (
                                <>
                                  <UserPlus className="w-4 h-4 mr-1" />
                                  Add
                                </>
                              )}
                            </Button>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                  
                  {searchQuery.length >= 2 && searchResults.length === 0 && !isSearching && (
                    <div className="text-center py-4">
                      <p className="text-white/70">No players found</p>
                    </div>
                  )}
                </div>
              )}
            </>
          )}
        </div>
        
        {/* Gift Modal */}
        {giftModal && (
          <div className="absolute inset-0 bg-black/80 flex items-center justify-center p-4 z-10">
            <Card className="w-full max-w-sm bg-gradient-to-br from-yellow-900 to-orange-900 border-yellow-500/50 p-4">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-bold text-white flex items-center gap-2">
                  <Gift className="w-5 h-5 text-yellow-400" />
                  Send Gift to {giftModal.friendName}
                </h3>
                <Button variant="ghost" size="sm" onClick={() => setGiftModal(null)} className="text-white">
                  <X className="w-4 h-4" />
                </Button>
              </div>
              
              <div className="space-y-3">
                <div className="flex items-center justify-between p-3 bg-white/10 rounded-lg">
                  <div className="flex items-center gap-2">
                    <Star className="w-5 h-5 text-yellow-400" />
                    <span className="text-white">Stars</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Input
                      type="number"
                      min="1"
                      max="100"
                      value={giftAmount}
                      onChange={(e) => setGiftAmount(parseInt(e.target.value) || 1)}
                      className="w-16 bg-white/10 border-white/20 text-white text-center"
                    />
                    <Button
                      size="sm"
                      onClick={() => sendGift(giftModal.friendId, "stars", giftAmount)}
                      disabled={actionLoading === `gift-${giftModal.friendId}`}
                      className="bg-yellow-500 hover:bg-yellow-600 text-black"
                    >
                      {actionLoading === `gift-${giftModal.friendId}` ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                      ) : (
                        <Send className="w-4 h-4" />
                      )}
                    </Button>
                  </div>
                </div>
                
                <p className="text-xs text-white/60 text-center">
                  Gifts are sent instantly and cannot be undone
                </p>
              </div>
            </Card>
          </div>
        )}
      </Card>
    </div>
  );
}
